"use client";
import { useTheme } from "next-themes";
import { m, LazyMotion } from "framer-motion";
import { useEffect, useState } from "react";
import Image from "next/image";

const loadDomMax = () => import("@/lib/motion/domMax").then((mod) => mod.default);

export default function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  const isDark = resolvedTheme === "dark";

  // small blob colors
  const sunBlob = "#F3F3F3";
  const moonBlob = "#0B0713";

  // Responsive blob sizes
  const blobSize =
    "w-[16px] h-[16px] lg:w-[18px] lg:h-[18px] laptop:w-[21px] laptop:h-[21px] 3xl:w-[24px] 3xl:h-[24px] 4xl:w-[27px] 4xl:h-[27px]";

  return (
    <LazyMotion features={loadDomMax} strict>
      <button
        onClick={() => setTheme(isDark ? "light" : "dark")}
        className={`flex items-center justify-between rounded-full
                    w-[42px] h-[22px]
                    lg:w-[48px] lg:h-[26px]
                    laptop:w-[56px] laptop:h-[31px]
                    3xl:w-[65px] 3xl:h-[36px]
                    4xl:w-[75px] 4xl:h-[41px]
                    px-1.5 lg:px-[6px] laptop:px-[8px] 3xl:px-[9px] 4xl:px-[10.5px]
                    transition-colors cursor-pointer
          ${
            isDark
              ? "bg-[#1D0424] shadow-[inset_1.5px_1.5px_5px_rgba(225,223,252,0.25)]"
              : "bg-[#E1DFFC] shadow-[inset_1.5px_1.5px_5px_rgba(0,0,0,0.25)]"
          }`}
      >
        {/* Sun Icon */}
        <div className="flex items-center justify-center rounded-full relative">
          {!isDark && (
            <m.div
              layoutId="theme-toggle-blob"
              initial={false}
              animate={{ backgroundColor: sunBlob }}
              transition={{ type: "spring", stiffness: 500, damping: 40 }}
              className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none ${blobSize} shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]`}
            />
          )}
          <div className="relative z-10 flex items-center justify-center w-full h-full">
            <Image
              src={
                isDark
                  ? "/assets/navbar/sun-dark.svg"
                  : "/assets/navbar/sun-light.svg"
              }
              alt="Sun"
              width={20}
              height={20}
              className="w-[11px] h-[11px] dark:w-[13px] dark:h-[13px] 
              lg:w-[13px] lg:h-[13px] dark:lg:w-[15px] dark:lg:h-[15px] 
              laptop:w-[16px] laptop:h-[16px] dark:laptop:w-[18px] dark:laptop:h-[18px] 
              3xl:w-[18px] 3xl:h-[18px] dark:3xl:w-[20px] dark:3xl:h-[20px] 
              4xl:w-[20px] 4xl:h-[20px] dark:4xl:w-[23px] dark:4xl:h-[23px] 
              transition-colors duration-300"
            />
          </div>
        </div>

        {/* Moon Icon */}
        <div className="flex items-center justify-center rounded-full relative">
          {isDark && (
            <m.div
              layoutId="theme-toggle-blob"
              initial={false}
              animate={{ backgroundColor: moonBlob }}
              transition={{ type: "spring", stiffness: 500, damping: 40 }}
              className={`
                absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none ${blobSize} shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]`}
            />
          )}
          <div className="relative z-10 flex items-center justify-center w-full h-full">
            <Image
              src={
                isDark
                  ? "/assets/navbar/moon-dark.svg"
                  : "/assets/navbar/moon-light.svg"
              }
              alt="Moon"
              width={20}
              height={20}
              className="w-[13px] h-[13.5px] dark:w-[10px] dark:h-[10px] 
              lg:w-[14px] lg:h-[14.5px] dark:lg:w-[12px] dark:lg:h-[12px] l
              aptop:w-[16px] laptop:h-[19.5px] dark:laptop:w-[14px] dark:laptop:h-[16px] 
              3xl:w-[19.5px] 3xl:h-[22px] dark:3xl:w-[18px] dark:3xl:h-[16px] 
              4xl:w-[21px] 4xl:h-[23px] dark:4xl:w-[16px] dark:4xl:h-[19px] 
              transition-colors duration-300"
            />
          </div>
        </div>
      </button>
    </LazyMotion>
  );
}
